/**
 * Renders the list of validation issues for the panel / modal (TASK-159 / AV-01).
 *
 * Each row leads with the rule's message, then where the field is and — when the
 * consumer passes `onLocationClick` — a "Přejít k poli" link that takes the
 * cataloguer there. The rule code closes the row in small print: it names the
 * rule for whoever maintains it and tells the cataloguer nothing (CZNA-1451).
 *
 * The message itself is the rule's Aleph text and stays verbatim; what the form
 * adds around it is context. A consumer that knows the form (`describeLocation`)
 * turns the bare `043` into the field's name and the tab it sits on, so the row
 * says what the formulář says. Without it — the detail panel, the MARC check —
 * the location is the bare tag, and with no `onLocationClick` it is plain text:
 * there is no form to jump into there.
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
var SEVERITY_COLORS = {
    BLOCKING: '#c0392b',
    WARNING: '#b8860b',
};
var formatLocation = function (loc) {
    if (!loc.tag)
        return null;
    var parts = [loc.tag];
    if (loc.subfieldCode)
        parts.push("$".concat(loc.subfieldCode));
    return parts.join('/');
};
export var ValidationIssueList = function (_a) {
    var issues = _a.issues, onLocationClick = _a.onLocationClick, describeLocation = _a.describeLocation;
    if (issues.length === 0) {
        return (React.createElement("p", { "data-test-validation-issue-list-empty": true, style: { color: '#666', margin: 0 } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.issues.empty" })));
    }
    return (React.createElement("ul", { "data-test-validation-issue-list": true, style: { listStyle: 'none', padding: 0, margin: 0 } }, issues.map(function (issue, idx) {
        var _a;
        var locText = formatLocation(issue.location);
        var described = locText ? describeLocation === null || describeLocation === void 0 ? void 0 : describeLocation(issue.location) : null;
        var location = (described === null || described === void 0 ? void 0 : described.fieldLabel) ? "".concat(locText, " ").concat(described.fieldLabel) : locText;
        return (React.createElement("li", { key: "".concat(issue.code, "-").concat(idx), "data-test-validation-issue": true, "data-test-validation-issue-code": issue.code, "data-test-validation-issue-severity": issue.severity, style: {
                padding: '8px 12px',
                borderLeft: "4px solid ".concat((_a = SEVERITY_COLORS[issue.severity]) !== null && _a !== void 0 ? _a : '#888'),
                background: '#fafafa',
                marginBottom: '6px',
                borderRadius: '2px',
            } },
            React.createElement("div", { style: { display: 'flex', gap: '8px', alignItems: 'baseline' } },
                React.createElement("span", { style: { flex: 1 }, "data-test-validation-issue-message": true }, issue.message),
                React.createElement("span", { "data-test-validation-issue-rule-code": true, style: {
                        fontFamily: 'monospace',
                        fontSize: '0.75em',
                        // #666 on the #fafafa row is 5.4:1 — small print still clears WCAG AA.
                        color: '#666',
                    } }, issue.code)),
            location && (React.createElement("div", { style: {
                    marginTop: '4px',
                    fontSize: '0.9em',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                } },
                React.createElement("span", { style: { color: '#444' }, "data-test-validation-issue-location": true }, (described === null || described === void 0 ? void 0 : described.tabLabel) ? (React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.issue.locationOnTab", values: { location: location, tab: described.tabLabel } })) : (React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.issue.location", values: { location: location } }))),
                onLocationClick && (!describeLocation || described) && (React.createElement("button", { type: "button", onClick: function () { return onLocationClick(issue.location); }, "data-test-validation-issue-jump": true, style: {
                        background: 'none',
                        border: 'none',
                        color: '#0073e6',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline',
                    } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.issue.jumpTo" })))))));
    })));
};
export default ValidationIssueList;
